// src/components/ui/file-input/index.tsx
import React, { useState, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import i18n from "@/utils/i18n";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { uploadAdminFiles } from "@/lib/files.api";
import { MULTIPART_THRESHOLD_BYTES, uploadFileMultipart } from "@/lib/multipartUploader";
import "./index.scss";
import {
  Pdf,
  FileLoading,
  FileAccepted,
  FileDelete,
  DocsFile,
  ImageFile,
} from "@/components/icons";
import Button from "@/components/ui/button";

export interface FileInputProps {
  label?: string;
  placeholder?: string;
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in MB
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  onFileSelect?: (files: File[]) => void;
  onFileRemove?: (index: number, fileId?: string) => void;
  supportedFormats?: string[];
  maxFiles?: number;
  showPreview?: boolean;
  variant?: "standard" | "outlined";
  existingFileUrl?: string; // For edit mode - existing file URL to display
  fileType?: "image" | "file"; // "image" for "Resim ekle", "file" for "Dosya ekle"
  /** Override the default button text (e.g. "Belge Ekle") */
  buttonLabel?: string;

  /** Klasör seçimi (webkitdirectory): kullanıcı bir klasör seçer, içindeki tüm dosyalar gelir */
  allowFolder?: boolean;
  /** Klasör seçildiğinde klasör adı (allowFolder ile birlikte kullanılır) */
  onFolderName?: (folderName: string) => void;

  // Upload management props
  fileUploadProgress?: Record<string, number>; // fileName -> progress percentage
  uploadedFiles?: Set<string>;
  onUploadStart?: (file: File) => void;
  onUploadProgress?: (fileName: string, progress: number) => void;
  onUploadComplete?: (fileName: string) => void;
  onUploadError?: (fileName: string, error: string) => void;

  enableDemoProgress?: boolean; // true = fake progress, false = API progress

  /** true = dosya seçildiğinde backend'e yükle (POST /api/admin/files/upload) */
  uploadToBackend?: boolean;
  /** Upload başarılı olduğunda (fileId, previewUrl - görsel için object URL) */
  onUploadSuccess?: (
    fileId: string,
    previewUrl?: string,
    meta?: { fileName?: string; mimeType?: string },
  ) => void;

  // Custom click handler - if provided, will be called instead of opening file dialog
  onClick?: () => void;
}

/**
 * Parent komponent'ler (örn. UnitAddModal'daki "Dosyayı Kaldır" butonu)
 * uploader dışından dosya kaldırabilsin diye ref üzerinden expose edilen API.
 */
export interface FileInputHandle {
  /** Backend upload sonrası aldığımız fileId ile dosyayı listeden sil.
   * Bulunamazsa sessizce yoksayar. onFileRemove callback'i tetiklenir. */
  removeFileById: (fileId: string) => void;
}

interface FileItem {
  file?: File;
  preview?: string;
  url?: string; // For existing files from URL
  isExisting?: boolean; // Flag to identify existing file
  /** Backend upload sonrası dönen fileId — onFileRemove çağrısında parent'a
   *  iletilir ki parent (örn. UnitAddModal) ilgili objective'i kesin olarak
   *  (index değil fileId eşleşmesiyle) silebilsin. */
  fileId?: string;
}

type DirHandle = { kind: string; name: string; entries(): AsyncIterableIterator<[string, DirHandle | FileHandle]> };
type FileHandle = { kind: string; getFile(): Promise<File> };

/** Sürükle-bırak ile atılan klasörün içindeki tüm dosyaları okur (File System Access API) */
async function readFilesFromDirectoryHandle(
  handle: DirHandle,
  basePath: string
): Promise<File[]> {
  const result: File[] = [];
  for await (const [name, entry] of handle.entries()) {
    const fullPath = basePath ? `${basePath}/${name}` : name;
    if (entry.kind === "file") {
      const file = await (entry as FileHandle).getFile();
      Object.defineProperty(file, "webkitRelativePath", { value: fullPath, writable: false });
      result.push(file);
    } else {
      const subFiles = await readFilesFromDirectoryHandle(entry as DirHandle, fullPath);
      result.push(...subFiles);
    }
  }
  return result;
}

interface FileEntryLike { isFile: boolean; isDirectory: boolean; name: string; file(cb: (f: File) => void, err?: (e: Error) => void): void }
interface DirEntryLike { createReader(): { readEntries(cb: (e: FileEntryLike[] | DirEntryLike[]) => void, err?: (e: Error) => void): void } }

/** Sürükle-bırak ile atılan klasörün içindeki tüm dosyaları okur (webkitGetAsEntry) */
function readFilesFromDirectoryEntry(
  entry: DirEntryLike & { name: string },
  basePath: string
): Promise<File[]> {
  return new Promise((resolve, reject) => {
    const result: File[] = [];
    const reader = (entry as DirEntryLike).createReader();

    function readBatch() {
      reader.readEntries(
        async (entries: (FileEntryLike | DirEntryLike)[]) => {
          if (entries.length === 0) {
            resolve(result);
            return;
          }
          for (const e of entries) {
            const fullPath = basePath ? `${basePath}/${(e as { name: string }).name}` : (e as { name: string }).name;
            if ((e as FileEntryLike).isFile) {
              const file = await new Promise<File>((res, rej) => (e as FileEntryLike).file(res, rej));
              Object.defineProperty(file, "webkitRelativePath", { value: fullPath, writable: false });
              result.push(file);
            } else {
              const subFiles = await readFilesFromDirectoryEntry(e as DirEntryLike & { name: string }, fullPath);
              result.push(...subFiles);
            }
          }
          readBatch();
        },
        (err: Error) => reject(err)
      );
    }
    readBatch();
  });
}

const FileInput = forwardRef<FileInputHandle, FileInputProps>(function FileInput(
  {
    label,
    placeholder = i18n.t("fileInput.placeholder"),
    accept,
    multiple = false,
    maxSize = 500, // 500MB default
    disabled = false,
    error = false,
    errorMessage,
    onFileSelect,
    onFileRemove,
    supportedFormats = ["PDF", "DOC", "DOCX", "XLS", "XLSX"],
    maxFiles = 1,
    variant = "standard",
    existingFileUrl,
    fileType = "file",
    buttonLabel,
    allowFolder = false,
    onFolderName,
    fileUploadProgress = {},
    uploadedFiles = new Set(),
    onUploadStart,
    onUploadComplete,
    enableDemoProgress = true, // Default olarak demo progress açık
    uploadToBackend = false,
    onUploadSuccess,
    onClick,
  },
  ref,
) {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [internalProgress, setInternalProgress] = useState<
    Record<string, number>
  >({});
  const [internalUploaded, setInternalUploaded] = useState<Set<string>>(
    new Set()
  );
  const [uploadErrors, setUploadErrors] = useState<Record<string, boolean>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Tracks in-flight multipart uploads so unmount or component error can
  // abort them (which also tells the backend to discard the S3 upload).
  const multipartAbortersRef = useRef<Map<string, AbortController>>(new Map());

  React.useEffect(() => {
    const aborters = multipartAbortersRef.current;
    return () => {
      aborters.forEach((controller) => controller.abort());
      aborters.clear();
    };
  }, []);

  // Klasör seçimi: input'a webkitdirectory attribute ekle/kaldır
  React.useEffect(() => {
    const el = fileInputRef.current;
    if (!el) return;
    if (allowFolder) {
      el.setAttribute("webkitdirectory", "");
    } else {
      el.removeAttribute("webkitdirectory");
    }
    return () => el.removeAttribute("webkitdirectory");
  }, [allowFolder]);

  // Initialize with existing file URL if provided
  React.useEffect(() => {
    if (existingFileUrl) {
      const isImage = accept?.includes("image") || false;
      setFiles((prevFiles) => {
        // Eğer yeni yüklenmiş dosyalar varsa (uploadToBackend ile yüklenen),
        // existingFileUrl zaten bu dosyanın preview'ı olabilir — tekrar ekleme
        const hasNewFiles = prevFiles.some((f) => !f.isExisting && f.file);
        if (hasNewFiles) return prevFiles;

        const hasExistingFile = prevFiles.some((f) => f.isExisting);

        if (!hasExistingFile) {
          const existingFile: FileItem = {
            url: existingFileUrl,
            isExisting: true,
            preview: isImage ? existingFileUrl : undefined,
          };
          return [existingFile];
        } else {
          // Update existing file URL if it changed
          return prevFiles.map((f) =>
            f.isExisting
              ? {
                  ...f,
                  url: existingFileUrl,
                  preview: isImage ? existingFileUrl : f.preview,
                }
              : f
          );
        }
      });
    } else {
      // Clear existing file if URL is removed, but keep new files
      setFiles((prevFiles) => prevFiles.filter((f) => !f.isExisting));
    }
  }, [existingFileUrl, accept]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const isValidFileType = useCallback(
    (file: File): boolean => {
      if (!accept) return true;
      const fileExtension = file.name.split(".").pop()?.toUpperCase();
      return fileExtension ? supportedFormats.includes(fileExtension) : false;
    },
    [accept, supportedFormats]
  );

  const isValidFileSize = useCallback(
    (file: File): boolean => {
      return file.size <= maxSize * 1024 * 1024;
    },
    [maxSize]
  );

  // Demo progress simulation
  const startDemoProgress = useCallback(
    (fileName: string) => {
      if (!enableDemoProgress) return;

      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 25 + 5; // 5-30% random artış

        if (progress >= 100) {
          progress = 100;
          clearInterval(interval);

          setInternalProgress((prev) => ({
            ...prev,
            [fileName]: progress,
          }));

          // 500ms sonra "completed" olarak işaretle
          setTimeout(() => {
            setInternalUploaded((prev) => new Set([...prev, fileName]));
            if (onUploadComplete) {
              onUploadComplete(fileName);
            }
          }, 500);
        } else {
          setInternalProgress((prev) => ({
            ...prev,
            [fileName]: Math.floor(progress),
          }));
        }
      }, 300); // Her 300ms'de güncelle
    },
    [enableDemoProgress]
  );

  // Files larger than the multipart threshold go directly to S3 via presigned
  // multipart PUTs (resilient to slow/flaky links). Smaller files keep the
  // single-POST flow that already works for documents/images.
  const runBackendUpload = useCallback(
    async (file: File) => {
      const fileName = file.name;
      setInternalProgress((prev) => ({ ...prev, [fileName]: 0 }));
      setUploadErrors((prev) => ({ ...prev, [fileName]: false }));

      const handleSuccess = (fileId: string) => {
        const previewUrl = file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : undefined;
        setInternalProgress((prev) => ({ ...prev, [fileName]: 100 }));
        setInternalUploaded((prev) => new Set([...prev, fileName]));
        setFiles((prev) =>
          prev.map((fi) => (fi.file === file ? { ...fi, fileId } : fi)),
        );
        onUploadComplete?.(fileName);
        onUploadSuccess?.(fileId, previewUrl, { fileName, mimeType: file.type });
      };

      const handleFailure = () => {
        setUploadErrors((prev) => ({ ...prev, [fileName]: true }));
        toast.error(i18n.t("fileInput.uploadFailed"));
      };

      if (file.size > MULTIPART_THRESHOLD_BYTES) {
        // Multipart path: real per-byte progress, resumable per part.
        const controller = new AbortController();
        multipartAbortersRef.current.set(fileName, controller);
        try {
          const result = await uploadFileMultipart(file, {
            signal: controller.signal,
            onProgress: (loaded, total) => {
              const pct = Math.min(99, Math.floor((loaded / total) * 100));
              setInternalProgress((prev) => ({ ...prev, [fileName]: pct }));
            },
          });
          handleSuccess(result.id);
        } catch (err) {
          if ((err as Error).name !== "AbortError") handleFailure();
        } finally {
          multipartAbortersRef.current.delete(fileName);
        }
        return;
      }

      // Small-file path: keep the existing fake-progress simulation so the UI
      // stays animated while the single POST is in flight.
      let progress = 0;
      progressIntervalRef.current = setInterval(() => {
        progress += Math.random() * 25 + 5;
        if (progress >= 95) {
          progress = 95;
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
        }
        setInternalProgress((prev) => ({ ...prev, [fileName]: Math.floor(progress) }));
      }, 300);

      try {
        const result = await uploadAdminFiles([file]);
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        if (result.length > 0) {
          handleSuccess(result[0].id);
        } else {
          handleFailure();
        }
      } catch {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        handleFailure();
      }
    },
    [onUploadComplete, onUploadSuccess]
  );

  const processFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const fileArray = Array.from(newFiles);
      const validFiles: FileItem[] = [];

      for (const file of fileArray) {
        // maxFiles sınırını yeni dosyalar bazında kontrol et (önceki yüklenenler sayılmaz)
        if (validFiles.length >= maxFiles) {
          break;
        }

        if (!isValidFileType(file)) {
          // Handle invalid file type error
          continue;
        }

        if (!isValidFileSize(file)) {
          // Handle file size error
          continue;
        }

        const fileItem: FileItem = {
          file,
        };

        // Create preview for images
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            if (e.target?.result) {
              fileItem.preview = e.target.result as string;
            }
          };
          reader.readAsDataURL(file);
        }

        validFiles.push(fileItem);

        if (uploadToBackend) {
          runBackendUpload(file);
        } else if (enableDemoProgress) {
          startDemoProgress(file.name);
        }

        if (onUploadStart) {
          onUploadStart(file);
        }
      }

      if (validFiles.length === 0) return;

      // Yeni dosya seçildiğinde önceki yüklenen (non-existing) dosyaları ve progress'i temizle
      setInternalProgress({});
      setInternalUploaded(new Set());
      setUploadErrors({});

      // isExisting (existingFileUrl'den gelen) dosyaları koru, yeni dosyaları ekle
      const updatedFiles = [
        ...files.filter((f) => f.isExisting),
        ...validFiles,
      ];
      setFiles(updatedFiles);

      if (onFileSelect) {
        onFileSelect(
          updatedFiles.filter((item) => item.file).map((item) => item.file!)
        );
      }
      // Klasör seçildiyse ilk dosyanın yolundan klasör adını çıkar
      if (allowFolder && onFolderName && validFiles.length > 0 && validFiles[0].file) {
        const first = validFiles[0].file as File & { webkitRelativePath?: string };
        const path = first.webkitRelativePath;
        if (path) {
          const folderName = path.split("/")[0] || first.name;
          onFolderName(folderName);
        }
      }
    },
    [
      files,
      maxFiles,
      isValidFileType,
      isValidFileSize,
      onFileSelect,
      onUploadStart,
      onFolderName,
      allowFolder,
      enableDemoProgress,
      startDemoProgress,
      uploadToBackend,
      runBackendUpload,
    ]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (disabled) return;

      const items = e.dataTransfer?.items;
      const dtItem = typeof DataTransferItem !== "undefined" ? DataTransferItem.prototype : null;
      const supportsHandle = dtItem && "getAsFileSystemHandle" in dtItem;
      const supportsEntry = dtItem && "webkitGetAsEntry" in dtItem;

      if (allowFolder && items && (supportsHandle || supportsEntry)) {
        const fileItems = [...items].filter((item) => item.kind === "file");
        const collectedFiles: File[] = [];

        for (let i = 0; i < fileItems.length; i++) {
          const item = fileItems[i];
          try {
            if (supportsHandle) {
              const handle = await (item as DataTransferItem & { getAsFileSystemHandle?(): Promise<DirHandle | FileHandle> }).getAsFileSystemHandle?.();
              if (handle?.kind === "directory") {
                const dirHandle = handle as DirHandle;
                const allFiles = await readFilesFromDirectoryHandle(dirHandle, dirHandle.name);
                if (allFiles.length > 0) {
                  onFolderName?.(dirHandle.name);
                  processFiles(allFiles);
                  return;
                }
              } else if (handle?.kind === "file") {
                const file = await (handle as FileHandle).getFile();
                collectedFiles.push(file);
              }
            } else if (supportsEntry) {
              const entry = (item as DataTransferItem & { webkitGetAsEntry?(): unknown }).webkitGetAsEntry?.() as (DirEntryLike & FileEntryLike & { name: string }) | null;
              if (entry?.isDirectory) {
                const allFiles = await readFilesFromDirectoryEntry(entry as unknown as DirEntryLike & { name: string }, entry.name);
                if (allFiles.length > 0) {
                  onFolderName?.(entry.name);
                  processFiles(allFiles);
                  return;
                }
              } else if (entry?.isFile) {
                const file = await new Promise<File>((res, rej) => (entry as unknown as FileEntryLike).file(res, rej));
                collectedFiles.push(file);
              }
            }
          } catch (err) {
            console.warn("Drop item read failed", err);
          }
        }
        if (collectedFiles.length > 0) {
          processFiles(collectedFiles);
        }
        return;
      }

      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles.length > 0) {
        processFiles(droppedFiles);
      }
    },
    [
      disabled,
      allowFolder,
      onFolderName,
      processFiles,
    ]
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      processFiles(selectedFiles);
      e.target.value = "";
    }
  };

  // Expose imperative API so parents can remove a file externally (by fileId).
  // UnitAddModal'daki "Dosyayı Kaldır" objective-level butonu bunu çağırır;
  // böylece hem FileInput'un internal listesi hem de parent'taki objectives
  // dizisi tek kaynaktan senkron kalır (onFileRemove callback'i içerde tetiklenir).
  useImperativeHandle(
    ref,
    () => ({
      removeFileById: (fileId: string) => {
        const idx = files.findIndex((f) => f.fileId === fileId);
        if (idx >= 0) removeFile(idx);
      },
    }),
    // files değişince ref'i yenile ki stale closure olmasın.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files],
  );

  const removeFile = (index: number) => {
    const fileToRemove = files[index];
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);

    // Clear file input to allow re-selection
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Remove from internal progress tracking
    if (fileToRemove?.file) {
      const name = fileToRemove.file.name;
      setInternalProgress((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
      setInternalUploaded((prev) => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
      setUploadErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }

    if (onFileRemove) {
      onFileRemove(index, fileToRemove?.fileId);
    }

    if (onFileSelect) {
      onFileSelect(
        updatedFiles.filter((item) => item.file).map((item) => item.file!)
      );
    }
  };

  const openFileDialog = () => {
    console.log("openFileDialog called, onClick:", !!onClick);
    if (disabled) return;
    
    // If custom onClick is provided, call it instead of opening file dialog
    if (onClick) {
      console.log("Calling custom onClick handler");
      onClick();
      return;
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const renderFileIcon = (fileItem: FileItem) => {
    // For existing files, determine extension from URL
    if (fileItem.isExisting && fileItem.url) {
      const urlExtension = fileItem.url
        .split(".")
        .pop()
        ?.split("?")[0]
        .toUpperCase();
      if (urlExtension === "PDF" || fileItem.url.includes(".pdf")) {
        return (
          <div className="file-icon pdf">
            <Pdf />
          </div>
        );
      }
      if (
        ["DOC", "DOCX"].includes(urlExtension || "") ||
        fileItem.url.includes(".doc")
      ) {
        return <div className="file-icon doc">DOC</div>;
      }
      if (
        ["XLS", "XLSX"].includes(urlExtension || "") ||
        fileItem.url.includes(".xls")
      ) {
        return <div className="file-icon xls">XLS</div>;
      }
      // For images, show preview
      if (
        fileItem.preview ||
        fileItem.url.match(/\.(jpg|jpeg|png|gif|webp)/i)
      ) {
        return (
          <div className="file-icon image-preview">
            <img src={fileItem.preview || fileItem.url} alt="Preview" />
          </div>
        );
      }
      return <div className="file-icon default">FILE</div>;
    }

    // For new files
    if (!fileItem.file) return null;
    const extension = fileItem.file.name.split(".").pop()?.toUpperCase();

    switch (extension) {
      case "PDF":
        return (
          <div className="file-icon pdf">
            <Pdf />
          </div>
        );
      case "DOC":
      case "DOCX":
        return <div className="file-icon doc">DOC</div>;
      case "XLS":
      case "XLSX":
        return <div className="file-icon xls">XLS</div>;
      default:
        return <div className="file-icon default">FILE</div>;
    }
  };

  // Get upload state for a specific file
  const getFileUploadState = (fileItem: FileItem) => {
    // Existing files are always considered uploaded
    if (fileItem.isExisting) {
      return {
        uploadProgress: 100,
        isUploaded: true,
        isUploading: false,
        hasError: false,
      };
    }

    if (!fileItem.file) {
      return {
        uploadProgress: undefined,
        isUploaded: false,
        isUploading: false,
        hasError: false,
      };
    }

    const fileName = fileItem.file.name;
    const externalProgress = fileUploadProgress[fileName];
    const externalUploaded = uploadedFiles.has(fileName);

    const progress =
      externalProgress !== undefined
        ? externalProgress
        : internalProgress[fileName];
    const isUploaded = externalUploaded || internalUploaded.has(fileName);
    const hasError = uploadErrors[fileName] ?? false;

    return {
      uploadProgress: progress,
      isUploaded,
      isUploading:
        progress !== undefined && progress < 100 && !isUploaded && !hasError,
      hasError,
    };
  };

  const getFileName = (fileItem: FileItem): string => {
    let fileName = "";

    if (fileItem.isExisting && fileItem.url) {
      // Extract filename from URL
      const urlParts = fileItem.url.split("/");
      fileName = urlParts[urlParts.length - 1].split("?")[0] || "Mevcut dosya";
    } else if (fileItem.file) {
      fileName = fileItem.file.name;
    }

    // Clamp filename to 10 characters
    if (fileName.length > 10) {
      const lastDotIndex = fileName.lastIndexOf(".");
      const hasExtension =
        lastDotIndex > 0 && lastDotIndex < fileName.length - 1;

      if (hasExtension) {
        const extension = fileName.substring(lastDotIndex + 1);
        const nameWithoutExt = fileName.substring(0, lastDotIndex);
        // Keep extension but limit name part to fit in 10 chars total
        const maxNameLength = Math.max(0, 10 - extension.length - 4); // 4 for "..."
        return (
          nameWithoutExt.substring(0, maxNameLength) + "..." + "." + extension
        );
      } else {
        // No extension, just truncate to 10 chars
        return fileName.substring(0, 7) + "...";
      }
    }

    return fileName;
  };

  const getFileSize = (fileItem: FileItem): string => {
    if (fileItem.isExisting) {
      return i18n.t("fileInput.uploaded");
    }
    return fileItem.file ? formatFileSize(fileItem.file.size) : "0 Bytes";
  };

  return (
    <div
      className={`file-input-container ${variant} ${error ? "error" : ""} ${disabled ? "disabled" : ""}`}
    >
      {label && <label className="file-input-label">{label}</label>}

      <div
        className={`file-drop-zone ${isDragOver ? "drag-over" : ""} ${files.length > 0 ? "has-files" : ""} ${existingFileUrl ? "has-existing-file" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <div className="drop-zone-content">
          <div className="upload-icon">
            {fileType === "image" ? <ImageFile /> : <DocsFile />}
          </div>
          <div className="drop-zone-text">
            <p>{placeholder}</p>
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              className="file-select-button"
              onClick={(e) => {
                e.stopPropagation();
                openFileDialog();
              }}
            >
              {buttonLabel ?? (fileType === "image" ? t("fileInput.addImage") : t("fileInput.addFile"))}
            </Button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={allowFolder || multiple}
          onChange={handleFileInputChange}
          style={{ display: "none" }}
          disabled={disabled}
        />
      </div>

      <div className="supported-formats">
        <span className="formats-link">
          {t("fileInput.supportedFormats")}
          <span className="formats-tooltip">{supportedFormats.join(", ")}</span>
        </span>
        <span className="max-size">{t("fileInput.maxSize")}: {maxSize} MB</span>
      </div>

      {files.length > 0 && (
        <div className="file-list">
          {files.map((fileItem, index) => {
            const { uploadProgress, isUploaded, isUploading, hasError } =
              getFileUploadState(fileItem);

            return (
              <div
                key={index}
                className={`file-item ${fileItem.isExisting ? "existing-file" : ""}`}
              >
                <div className="file-item-header">
                  <div className="file-info">
                    {renderFileIcon(fileItem)}
                    <div className="file-details">
                      <span className="file-name">{getFileName(fileItem)}</span>
                      <span className="file-size">{getFileSize(fileItem)}</span>
                    </div>
                  </div>

                  <div className="file-actions">
                    {isUploading && uploadProgress !== undefined && (
                      <>
                        <div className="upload-status uploading">
                          <FileLoading />
                          {t("fileInput.uploading")}
                        </div>
                        <span className="progress-percentage">
                          %{uploadProgress}
                        </span>
                      </>
                    )}

                    {isUploaded && (
                      <div className="upload-status completed">
                        <FileAccepted />
                        Tamamlandı
                      </div>
                    )}

                    {hasError && (
                      <div className="upload-status error">
                        Yükleme başarısız oldu
                      </div>
                    )}

                    <button
                      type="button"
                      className="remove-file-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                    >
                      <FileDelete />
                    </button>
                  </div>
                </div>

                {isUploading && uploadProgress !== undefined && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {error && errorMessage && (
        <span className="file-input-error">{errorMessage}</span>
      )}
    </div>
  );
});

export default FileInput;
